// src/components/Stopwatch.jsx
//
// Optionale Stoppuhr fuer Zeitspiele. Sie ist eine reine EINGABEHILFE: am Ende
// uebergibt sie ueber onApply() eine Zahl in Sekunden (zwei Nachkommastellen),
// die im Ergebnisdialog ganz normal ins Zahlenfeld geschrieben wird. Der Wert
// bleibt danach von Hand editierbar, und die direkte Eingabe ohne Stoppuhr
// funktioniert unveraendert weiter.
//
// Zwei Modi:
//   • "total"      – eine Uhr misst die Gesamtzeit des Teams.
//   • "per_member" – je Spieler eine eigene Zeit; uebernommen wird die Summe.
//
// VERSUCHE (nur "per_member", `maxVersuche` > 1, je Station einstellbar): Je
// Spieler lassen sich bis zu `maxVersuche` Zeiten per "Versuch speichern"
// ablegen; die Uhr steht danach wieder auf 0. Zur Summe zaehlt je Spieler der
// BESTE Wert aus gespeicherten Versuchen und der aktuell gestoppten Zeit –
// "bester" nach der Wertungsrichtung der Station (`scoreDirection`: "lower" =
// kuerzeste Zeit, "higher" = laengste). Bei 1 (Standard) gibt es keine
// Versuche – genau ein Lauf wie bisher.
//
// MAXIMALZEIT (optional, `maxSeconds`): Erreicht eine Uhr die Maximalzeit, stoppt
// sie von selbst und zaehlt genau diese Zeit. Im Modus "total" wird der Wert
// dann sofort uebernommen; bei "per_member" gilt die Grenze je Teilnehmer, und
// die Summe wird wie gewohnt per "Uebernehmen" eingetragen. Wie beim Countdown
// klingelt es, sobald die Maximalzeit erreicht ist (Ton im Browser erzeugt, siehe
// utils/klingeln.js – nur bei sichtbarer Seite).
//
// GENAUIGKEIT: Gemessen wird ueber Zeitstempel (Date.now()), NICHT durch
// Hochzaehlen eines Intervalls. Auf einem Handy drosselt der Browser Timer im
// Hintergrund oder bei gesperrtem Display – ein gezaehltes Intervall ginge dann
// nach. Das Intervall hier dient nur der Anzeige; der uebernommene Wert wird
// immer aus den Zeitstempeln berechnet.

import { useEffect, useMemo, useRef, useState } from "react";
import { Play, Pause, RotateCcw, Check, ChevronDown, BellOff, Save } from "lucide-react";
import Button from "./ui/Button";
import useWakeLock from "../hooks/useWakeLock";
import { audioFreischalten, klingeln } from "../utils/klingeln";

// Ein Team spielt jedes Spiel mit genau 4 Personen. Hat ein Team 5 Mitglieder,
// pausiert eines – es werden dann 4 aus 5 ausgewaehlt.
const SPIELER_PRO_SPIEL = 4;

const LS_EINGEKLAPPT = "stopwatch_eingeklappt";

const zweiStellen = (sekunden) => Math.round(sekunden * 100) / 100;
const alsText = (sekunden) => zweiStellen(sekunden).toFixed(2);
// Fuer die Anzeige mit deutschem Komma. `alsText` bleibt fuer das Uebernehmen
// ins Zahlenfeld (type="number" erwartet einen Punkt).
const alsAnzeige = (sekunden) => alsText(sekunden).replace(".", ",");

// Laufende Millisekunden einer Bahn: bereits gestoppte Zeit plus – falls sie
// gerade laeuft – die Zeit seit dem letzten Start. Mit Maximalzeit (`maxMs` > 0)
// nie mehr als diese, auch wenn der Browser den Timer gedrosselt hat und das
// Stoppen erst verspaetet erkannt wird.
function verstricheneMs(bahn, jetzt, maxMs = 0) {
  const ms = bahn.accumulatedMs + (bahn.startTs ? jetzt - bahn.startTs : 0);
  return maxMs > 0 ? Math.min(ms, maxMs) : ms;
}

// Wert, der fuer einen Spieler zaehlt: der beste aus gespeicherten Versuchen und
// der aktuellen Messung (sofern > 0). Ohne Versuche einfach die aktuelle Zeit.
function gewerteteMs(bahn, jetzt, maxMs, hoeherBesser) {
  const aktuell = verstricheneMs(bahn, jetzt, maxMs);
  const versuche = bahn.versuche || [];
  if (versuche.length === 0) return aktuell;
  const kandidaten = aktuell > 0 ? [...versuche, aktuell] : versuche;
  return hoeherBesser ? Math.max(...kandidaten) : Math.min(...kandidaten);
}

const leereBahn = () => ({ accumulatedMs: 0, startTs: null, versuche: [] });

export default function Stopwatch({
  mode,
  memberNames = [],
  maxSeconds = 0,
  scoreDirection = "lower",
  maxVersuche = 1,
  onApply,
  disabled = false,
}) {
  const maxMs = Math.max(0, Math.round(Number(maxSeconds) || 0)) * 1000;
  const hoeherBesser = scoreDirection === "higher";
  const versucheMax = Math.max(1, Math.round(Number(maxVersuche) || 1));
  const mitVersuchen = mode === "per_member" && versucheMax > 1;

  // Im Team-pro-Spieler-Modus: welche Mitglieder spielen mit? Standard sind die
  // ersten vier. Bei genau vier (oder weniger) Mitgliedern ist nichts zu
  // waehlen; erst ab fuenf muss eines pausieren.
  const [aktiveIndizes, setAktiveIndizes] = useState(() =>
    memberNames.slice(0, SPIELER_PRO_SPIEL).map((_, i) => i)
  );

  // Eine "Bahn" je Uhr: total → genau eine; per_member → eine je Mitglied
  // (Index deckungsgleich mit memberNames). { accumulatedMs, startTs, versuche }.
  const [bahnen, setBahnen] = useState(() =>
    (mode === "per_member" ? memberNames : [null]).map(leereBahn)
  );

  // Eingeklappt-Zustand wird am Geraet gemerkt: wer die Stoppuhr nicht nutzt,
  // soll sie nicht bei jedem Team neu wegklicken muessen (die Komponente wird
  // je Team neu aufgebaut).
  const [eingeklappt, setEingeklappt] = useState(() => {
    try {
      return localStorage.getItem(LS_EINGEKLAPPT) === "1";
    } catch {
      return false;
    }
  });
  const einklappenSetzen = (wert) => {
    setEingeklappt(wert);
    try {
      localStorage.setItem(LS_EINGEKLAPPT, wert ? "1" : "0");
    } catch {
      /* Speicher gesperrt – nur fuer diese Sitzung */
    }
  };

  // Tickt nur, damit laufende Uhren neu gerendert werden; kein Rechenwert.
  const [, setTick] = useState(0);
  const intervalRef = useRef(null);

  // Klingeln bei Erreichen der Maximalzeit.
  const [klingelt, setKlingelt] = useState(false);
  const stopKlingelnRef = useRef(null);
  const klingelnBeenden = () => {
    if (stopKlingelnRef.current) {
      stopKlingelnRef.current();
      stopKlingelnRef.current = null;
    }
    setKlingelt(false);
  };
  // Beim Schliessen des Dialogs (oder Teamwechsel) nicht weiterklingeln.
  useEffect(
    () => () => {
      if (stopKlingelnRef.current) stopKlingelnRef.current();
    },
    []
  );

  // Wechselt das Team (andere Mitgliederliste), alles zuruecksetzen.
  useEffect(() => {
    setAktiveIndizes(memberNames.slice(0, SPIELER_PRO_SPIEL).map((_, i) => i));
    setBahnen((mode === "per_member" ? memberNames : [null]).map(leereBahn));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, memberNames.join(" ")]);

  const laeuftIrgendwo = bahnen.some((b) => b.startTs !== null);

  // Aktueller Stand fuer den Timer (dessen Closure sonst veraltet waere).
  const bahnenRef = useRef(bahnen);
  bahnenRef.current = bahnen;
  const onApplyRef = useRef(onApply);
  onApplyRef.current = onApply;

  // Anzeige-Intervall nur laufen lassen, solange wirklich eine Uhr laeuft.
  useEffect(() => {
    if (laeuftIrgendwo && !intervalRef.current) {
      intervalRef.current = setInterval(() => {
        // Maximalzeit erreicht? Dann diese Uhr(en) auf genau maxMs anhalten.
        if (maxMs > 0) {
          const jetztTs = Date.now();
          const erreicht = (b) => b.startTs && b.accumulatedMs + (jetztTs - b.startTs) >= maxMs;
          if (bahnenRef.current.some(erreicht)) {
            // Ref sofort mitziehen: folgt ein weiterer Tick vor dem naechsten
            // Rendern, darf das Uebernehmen nicht ein zweites Mal ausloesen.
            const neu = bahnenRef.current.map((b) =>
              erreicht(b) ? { ...b, accumulatedMs: maxMs, startTs: null } : b
            );
            bahnenRef.current = neu;
            setBahnen(neu);
            if (stopKlingelnRef.current) stopKlingelnRef.current();
            setKlingelt(true);
            stopKlingelnRef.current = klingeln(() => {
              stopKlingelnRef.current = null;
              setKlingelt(false);
            });
            // Gesamtzeit: die Maximalzeit ist das Ergebnis – gleich uebernehmen.
            if (mode !== "per_member") onApplyRef.current?.(alsText(maxMs / 1000));
            return;
          }
        }
        setTick((t) => t + 1);
      }, 100);
    } else if (!laeuftIrgendwo && intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [laeuftIrgendwo, maxMs, mode]);

  // Bildschirm waehrend der Messung wachhalten (siehe hooks/useWakeLock.js).
  const bildschirmWach = useWakeLock(laeuftIrgendwo);

  const jetzt = Date.now();

  const starten = (i) => {
    if (disabled) return;
    // Muss im Klick laufen: erst dadurch ist der Klingelton spaeter erlaubt.
    if (maxMs > 0) audioFreischalten();
    setBahnen((prev) =>
      prev.map((b, idx) =>
        idx === i &&
        !b.startTs &&
        !(maxMs > 0 && b.accumulatedMs >= maxMs) &&
        (b.versuche || []).length < versucheMax
          ? { ...b, startTs: Date.now() }
          : b
      )
    );
  };
  const stoppen = (i) => {
    setBahnen((prev) =>
      prev.map((b, idx) =>
        idx === i && b.startTs
          ? {
              ...b,
              accumulatedMs:
                maxMs > 0
                  ? Math.min(maxMs, b.accumulatedMs + (Date.now() - b.startTs))
                  : b.accumulatedMs + (Date.now() - b.startTs),
              startTs: null,
            }
          : b
      )
    );
  };
  const zuruecksetzen = (i) => {
    klingelnBeenden();
    setBahnen((prev) => prev.map((b, idx) => (idx === i ? { ...b, accumulatedMs: 0, startTs: null } : b)));
  };
  // Aktuelle (gestoppte) Zeit als Versuch ablegen; die Uhr steht danach auf 0.
  const versuchSpeichern = (i) => {
    klingelnBeenden();
    setBahnen((prev) =>
      prev.map((b, idx) =>
        idx === i && !b.startTs && b.accumulatedMs > 0 && b.versuche.length < versucheMax
          ? { accumulatedMs: 0, startTs: null, versuche: [...b.versuche, b.accumulatedMs] }
          : b
      )
    );
  };
  const versuchLoeschen = (i, nr) => {
    setBahnen((prev) =>
      prev.map((b, idx) => (idx === i ? { ...b, versuche: b.versuche.filter((_, k) => k !== nr) } : b))
    );
  };

  // Welche Bahnen zaehlen zur Summe? Bei per_member nur die aktiven Spieler.
  const relevanteIndizes = useMemo(() => {
    if (mode !== "per_member") return [0];
    return aktiveIndizes;
  }, [mode, aktiveIndizes]);

  const summeSekunden = useMemo(() => {
    return relevanteIndizes.reduce(
      (summe, i) =>
        summe + gewerteteMs(bahnen[i] || leereBahn(), jetzt, maxMs, hoeherBesser) / 1000,
      0
    );
    // jetzt bewusst als Abhaengigkeit, damit die laufende Summe live mitzaehlt.
  }, [relevanteIndizes, bahnen, jetzt, maxMs, hoeherBesser]);

  const zuVieleAktiv = mode === "per_member" && aktiveIndizes.length > SPIELER_PRO_SPIEL;
  const kannUebernehmen = !disabled && !zuVieleAktiv && summeSekunden > 0 && !laeuftIrgendwo;

  const toggleSpieler = (i) => {
    setAktiveIndizes((prev) =>
      prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]
    );
  };

  const uebernehmen = () => {
    if (!kannUebernehmen) return;
    onApply(alsText(summeSekunden));
  };

  // ---- Darstellung einer einzelnen Uhr-Bahn ----
  const bahnZeile = (i, label) => {
    const b = bahnen[i] || leereBahn();
    const laeuft = b.startTs !== null;
    const sekunden = verstricheneMs(b, jetzt, maxMs) / 1000;
    const maxErreicht = maxMs > 0 && !laeuft && b.accumulatedMs >= maxMs;
    const aktiv = mode !== "per_member" || aktiveIndizes.includes(i);
    const versuche = b.versuche || [];
    const versucheVoll = versuche.length >= versucheMax;
    // Bester gespeicherter Versuch wird hervorgehoben – aber nur, wenn die
    // aktuelle Messung ihn nicht schlaegt.
    const besterMs = versuche.length > 0 ? gewerteteMs(b, jetzt, maxMs, hoeherBesser) : null;
    const besterIdx = besterMs === null ? -1 : versuche.indexOf(besterMs);

    // Start/Stopp bewusst als GROSSER Button mit Textlabel (nicht nur Icon)
    // und deutlich abgesetzt vom kleineren Reset-Button daneben – genau das
    // fehlte vorher: Start/Stopp und Reset lagen als zwei aehnlich kleine
    // Icon-Buttons eng nebeneinander und liessen sich in der Hektik am Stand
    // leicht verwechseln bzw. es wurde versehentlich daneben getippt.
    return (
      <div
        key={i}
        className={`rounded-lg border px-3 py-3 ${
          aktiv ? "border-secondary-700 bg-secondary-800" : "border-secondary-800 bg-secondary-900 opacity-50"
        }`}
      >
        <div className="mb-2 flex items-center justify-between gap-2">
          {label ? (
            <span className="flex-1 truncate text-sm text-gray-200" title={label}>
              {label}
            </span>
          ) : (
            <span />
          )}
          <span
            className={`font-mono tabular-nums ${label ? "text-xl" : "text-3xl"} ${
              laeuft ? "text-primary-400" : "text-white"
            }`}
          >
            {alsAnzeige(sekunden)} s
          </span>
        </div>
        {maxErreicht && (
          <p className="mb-2 text-xs font-semibold text-amber-400">
            Maximalzeit erreicht – Uhr gestoppt.
          </p>
        )}
        {mitVersuchen && versuche.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {versuche.map((ms, nr) => (
              <span
                key={nr}
                className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-mono text-xs tabular-nums ${
                  nr === besterIdx ? "bg-green-700 text-white" : "bg-secondary-700 text-gray-300"
                }`}
                title={nr === besterIdx ? "Bester Versuch – zählt" : undefined}
              >
                {nr + 1}. {alsAnzeige(ms / 1000)} s{nr === besterIdx && " ★"}
                <button
                  type="button"
                  onClick={() => versuchLoeschen(i, nr)}
                  disabled={disabled}
                  title={`Versuch ${nr + 1} löschen`}
                  className="ml-0.5 text-gray-400 hover:text-white"
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        {aktiv && (
          <div className="flex items-stretch gap-3">
            {!laeuft ? (
              <button
                type="button"
                onClick={() => starten(i)}
                disabled={disabled || maxErreicht || (mitVersuchen && versucheVoll)}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 py-4 text-base font-bold text-white hover:bg-green-500 active:bg-green-700 disabled:opacity-50"
              >
                <Play className="h-6 w-6" /> Start
              </button>
            ) : (
              <button
                type="button"
                onClick={() => stoppen(i)}
                className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-600 py-4 text-base font-bold text-white hover:bg-amber-500 active:bg-amber-700"
              >
                <Pause className="h-6 w-6" /> Stopp
              </button>
            )}
            <button
              type="button"
              onClick={() => zuruecksetzen(i)}
              disabled={disabled || (b.accumulatedMs === 0 && !laeuft)}
              title="Zurücksetzen"
              className="flex w-14 flex-none items-center justify-center rounded-lg bg-secondary-700 text-gray-300 hover:bg-secondary-600 disabled:opacity-40"
            >
              <RotateCcw className="h-5 w-5" />
            </button>
          </div>
        )}
        {aktiv && mitVersuchen && (
          <button
            type="button"
            onClick={() => versuchSpeichern(i)}
            disabled={disabled || laeuft || b.accumulatedMs === 0 || versucheVoll}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-lg bg-secondary-700 py-2 text-sm font-semibold text-gray-200 hover:bg-secondary-600 disabled:opacity-40"
          >
            <Save className="h-4 w-4" />
            {versucheVoll
              ? `${versucheMax} Versuche gespeichert`
              : `Versuch speichern (${versuche.length + 1}/${versucheMax})`}
          </button>
        )}
      </div>
    );
  };

  // Eingeklappt bleibt die Komponente gemountet (nur versteckt), damit gestoppte
  // Zeiten erhalten bleiben. Waehrend eine Uhr laeuft, ist Einklappen gesperrt –
  // eine unsichtbar laufende Uhr waere am Stand eine Fehlerquelle.
  const offen = !eingeklappt || laeuftIrgendwo || klingelt;

  return (
    <div className="rounded-lg border border-primary-700/40 bg-secondary-900/60">
      <button
        type="button"
        onClick={() => einklappenSetzen(!eingeklappt)}
        disabled={laeuftIrgendwo}
        aria-expanded={offen}
        className="flex w-full items-center justify-between gap-2 p-3 text-left disabled:cursor-default"
      >
        <span className="flex items-center gap-1 text-xs font-semibold uppercase tracking-wide text-primary-400">
          <ChevronDown className={`h-4 w-4 transition-transform ${offen ? "" : "-rotate-90"}`} />
          Stoppuhr {mode === "per_member" ? "(pro Teilnehmer)" : "(Gesamtzeit)"}
          {maxMs > 0 && ` · max. ${maxMs / 1000} s${mode === "per_member" ? " je Teilnehmer" : ""}`}
        </span>
        <span className="text-xs text-secondary-400">
          {!offen && summeSekunden > 0 ? `${alsAnzeige(summeSekunden)} s` : "optional"}
        </span>
      </button>

      <div className={`space-y-2 px-3 pb-3 ${offen ? "" : "hidden"}`}>
      {klingelt && (
        <button
          type="button"
          onClick={klingelnBeenden}
          className="flex w-full animate-pulse items-center justify-center gap-2 rounded-lg bg-red-600 py-4 text-base font-bold text-white hover:bg-red-500 active:bg-red-700"
        >
          <BellOff className="h-6 w-6" /> Klingeln aus
        </button>
      )}
      {mitVersuchen && memberNames.length > 0 && (
        <p className="text-xs text-secondary-400">
          Bis zu {versucheMax} Versuche je Spieler speicherbar – es zählt der beste (
          {hoeherBesser ? "längste" : "kürzeste"} Zeit).
        </p>
      )}
      {mode === "per_member" && memberNames.length > SPIELER_PRO_SPIEL && (
        <p className="text-xs text-secondary-400">
          Genau {SPIELER_PRO_SPIEL} Spieler auswaehlen – die uebrigen pausieren.
        </p>
      )}

      {mode === "per_member" ? (
        memberNames.length === 0 ? (
          <p className="text-sm text-secondary-400">Fuer dieses Team sind keine Mitglieder hinterlegt.</p>
        ) : (
          <div className="space-y-3">
            {memberNames.map((name, i) => (
              <div key={i} className="flex items-start gap-2">
                {memberNames.length > SPIELER_PRO_SPIEL && (
                  <input
                    type="checkbox"
                    checked={aktiveIndizes.includes(i)}
                    onChange={() => toggleSpieler(i)}
                    disabled={disabled}
                    className="mt-4 h-4 w-4 accent-primary-500"
                    title={aktiveIndizes.includes(i) ? "Spielt mit" : "Pausiert"}
                  />
                )}
                <div className="flex-1">{bahnZeile(i, name || `Spieler ${i + 1}`)}</div>
              </div>
            ))}
          </div>
        )
      ) : (
        bahnZeile(0, null)
      )}

      <div className="flex items-center justify-between pt-1">
        <div className="text-sm text-gray-300">
          {mode === "per_member" ? (
            <>
              Summe:{" "}
              <span className="font-mono font-semibold text-white">{alsAnzeige(summeSekunden)} s</span>
            </>
          ) : (
            <span className="text-secondary-400">Bei Stopp uebernehmbar</span>
          )}
        </div>
        <Button
          type="button"
          variant="primary"
          onClick={uebernehmen}
          disabled={!kannUebernehmen}
          className="!px-3 !py-1.5"
        >
          <Check className="mr-1 h-4 w-4" /> Übernehmen
        </Button>
      </div>

      {zuVieleAktiv && (
        <p className="text-xs text-status-error">
          Es sind {aktiveIndizes.length} Spieler aktiv – bitte auf {SPIELER_PRO_SPIEL} reduzieren.
        </p>
      )}
      {laeuftIrgendwo && (
        <p className="text-xs text-secondary-400">
          Zum Übernehmen zuerst alle Uhren stoppen.
          {bildschirmWach && " · Bildschirm bleibt an."}
        </p>
      )}
      </div>
    </div>
  );
}
